import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { FiUploadCloud } from "react-icons/fi";
import { toast } from "react-toastify";
import { canvasImageStore } from "../../stores/ImageStore";
import ImagesGrid from "../../Components/ImagesGrid";
import UploadResponseModal from "../Account/UploadResponseModal";
import loadingSpinner from "../../assets/img/Spinner.svg";
import { BANNER_PHOTO_TYPE } from "../../Services/BannerService";
import { addPhoto } from "./BannerWorkspace";

const VALID_TYPES = ["image/jpg", "image/jpeg", "image/png", "image/gif"];

const BannerUploadPanel = observer(() => {
  const [isUploading, setIsUploading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);

  const loadImages = () => canvasImageStore.retrievePhotos(BANNER_PHOTO_TYPE, "", "");

  useEffect(() => {
    loadImages();
  }, []);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      if (!VALID_TYPES.includes(files[i].type)) {
        toast.error(`File type ${files[i].type} is not allowed.`, { position: "top-right" });
        return;
      }
      formData.append("files", files[i]);
    }
    event.target.value = "";
    setIsUploading(true);
    await canvasImageStore.uploadImages(formData, true);
    if (canvasImageStore.error) {
      toast.error(canvasImageStore.error, { position: "top-right" });
    } else {
      setModalOpen(true);
      await loadImages();
    }
    setIsUploading(false);
  };

  const handleDelete = async (id: number) => {
    setIsUploading(true);
    await canvasImageStore.deleteImage(id);
    if (canvasImageStore.error) toast.error("Error deleting image.", { position: "top-right" });
    else await loadImages();
    setIsUploading(false);
  };

  return (
    <div className="relative flex h-full flex-col font-jakarta">
      <div className="border-b border-gray-200 p-3">
        <h2 className="text-sm font-bold text-ink">Photos</h2>
        <p className="mb-3 text-xs text-gray-500">Upload a photo, then click it or drag it onto your banner.</p>
        <label
          htmlFor="banner-image-upload"
          className="flex cursor-pointer items-center justify-center gap-2 rounded border border-dashed border-brand bg-brand-soft py-2 text-xs font-semibold text-brand hover:bg-brand-mist"
        >
          <FiUploadCloud size={16} /> Upload Photos
          <input
            id="banner-image-upload" type="file" multiple className="hidden"
            accept="image/jpg,image/jpeg,image/png,image/gif"
            onChange={handleUpload}
          />
        </label>
      </div>

      <div className="flex-1 overflow-y-auto">
        <ImagesGrid
          images={canvasImageStore.images}
          onSelect={item => addPhoto(item)}
          onDelete={handleDelete}
          isLoading={canvasImageStore.isLoading}
          itemHeight={100}
        />
      </div>

      {isUploading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75">
          <img src={loadingSpinner} alt="Loading..." className="h-16 w-16" />
        </div>
      )}

      <UploadResponseModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
});

export default BannerUploadPanel;
